import React, { useEffect, useRef } from 'react';
import MasonryBase from 'react-masonry-component';
const Masonry = MasonryBase as any;

const TAG_CONFIG: Record<string, { icon: string; label: string }> = {
  'scenic':          { icon: 'ti-mountain',        label: 'Scenic' },
  'cafe-on-site':    { icon: 'ti-coffee',           label: 'Café on site' },
  'family-friendly': { icon: 'ti-friends',          label: 'Family friendly' },
  'good-for-walk':   { icon: 'ti-walk',             label: 'Good for a walk' },
  'dog-friendly':    { icon: 'ti-paw',              label: 'Dog friendly' },
  'farm-shop':       { icon: 'ti-building-store',   label: 'Farm shop' },
  'good-restaurant': { icon: 'ti-tools-kitchen-2',  label: 'Good restaurant' },
};

const PlacesMasonryGallery = ({ items, setItems, onItemClick, selectedPlace, isMapView }) => {
  const masonryRef = useRef(null);
  
  const masonryOptions = {
    transitionDuration: 0,
    columnWidth: '.image-element-class',
  } as any;

  // Resilient image handlers preserved, now using item.id
  const handleItemRemove = (id) => {
    setItems(prev => prev.filter(item => item.id !== id));
  };
  
  
  const handleImageErrorWithStatus = (event, id) => {
    const img = event.target;
    if (img.naturalWidth === 0 && img.naturalHeight === 0) {
      console.log(`Image failed to load for item ${id}: ${img.src}`);
      handleItemRemove(id);
    }
  };

  const handleImageLoad = (event, id) => {
    const img = event.target;
    if (img.naturalWidth < 50 || img.naturalHeight < 50) {
      handleItemRemove(id);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (masonryRef.current) {
        masonryRef.current.masonry.layout();  // Force Masonry to re-layout
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!isMapView && masonryRef.current) {
      masonryRef.current.masonry.reloadItems();  // Reload items
      masonryRef.current.masonry.layout();  // Force Masonry to re-layout
    }
  }, [isMapView]);

  // Cold-start fix: trigger layout when gallery first receives content (0 → populated).
  // Fixes the case where no hub content exists and AI results are the first items to arrive.
  const prevItemsLengthRef = useRef(0);
  useEffect(() => {
    if (items.length > 0 && prevItemsLengthRef.current === 0 && masonryRef.current) {
      masonryRef.current.masonry.reloadItems();
      masonryRef.current.masonry.layout();
    }
    prevItemsLengthRef.current = items.length;
  }, [items.length]);

  return (
    <div>
    <Masonry
      className={'my-gallery-class'} // default ''
      elementType={'div'} // default 'div'
      options={masonryOptions} // default {}
      disableImagesLoaded={false} // default false
      updateOnEachImageLoad={false} // default false and works only if disableImagesLoaded is false
      ref={masonryRef}
    >
        {/* Sizer element — gives Masonry a persistent column-width reference even when no PHOTO items are present */}
        <div className="image-element-class" style={{ position: 'absolute', visibility: 'hidden', height: 0 }} />
        {items.map((item) => {
          // --- RENDER LOGIC BY TYPE ---
          
          
			// 1. STANDARD PHOTO
			if (item.type === 'PHOTO') {
			  const photo = item.data;
			  return (
				<React.Fragment key={item.id}>
				  <div className="image-element-class">
					<a href={photo.contextLink} target="_blank" rel="noopener noreferrer">
					  <img
						src={photo.link}
						alt={photo.title}
						onError={(e) => handleImageErrorWithStatus(e, item.id)}
						onLoad={(e) => handleImageLoad(e, item.id)}
					  />
	  
					  {photo.title && photo.is_hub_hero && (
						<div className="heading-overlay" style={{ opacity: 1 }}>
						  <div className="heading-title">{photo.title}</div>
						</div>
					  )}
	  
					  {photo.title && (
						<div className="image-color-bar" style={{
						  backgroundColor: photo.fillColor ? `color-mix(in srgb, ${photo.fillColor} 60%, transparent)` : 'rgba(238, 238, 238, 0.6)',
						  color: photo.textColor || '#000'
						}}>
						  <div className="image-title">{photo.title}</div>
						  {photo.attribution && <div className="image-attribution">{photo.attribution}</div>}
						</div>
					  )}
					</a>
				  </div>

				  {/* RESTORED: The companion Article Box */}
				  {photo.article_body && (
					<div
					  className="article-element-class"
					  dangerouslySetInnerHTML={{ __html: photo.article_body }}
					  style={{
						border: photo.fillColor
						  ? `4px solid color-mix(in srgb, ${photo.fillColor} 60%, transparent)`
						  : '4px solid rgba(238, 238, 238, 0.6)'
					  }}
					/>
				  )}
				</React.Fragment>
			  );
			}

            // 2. AI SUGGESTED PLACE (Hidden Gem)
			if (item.type === 'AI_PLACE') {
			  const place = item.data; 
			  // IMPORTANT: We now pull the image from the first element of the images array
			  const heroImage = place.images && place.images.length > 0 ? place.images[0] : null;
			  console.log('PlacesMasonryGallery AI Place heroImage='+heroImage);

              const isSelected = selectedPlace && selectedPlace.place_id === place.place_id;
              
			  return (
				<div
					key={item.id}
					data-place-id={place.place_id}
					className={`article-element-class m-card ai-place-card ${isSelected ? 'selected' : ''}`}
					onClick={() => onItemClick && onItemClick(place)}
					style={{ cursor: 'pointer' }}
				  >
				  {/* IMAGE */}
				  {heroImage && heroImage.image_url && (
					<div>
					  <img
						src={heroImage.image_url}
						className="ai-card-img"
						alt={place.title}
						style={{ width: '100%', display: 'block' }}
						onError={(e) => (e.target.parentNode.style.display = 'none')}
					  />
					  {/* ATTRIBUTION */}
					  {heroImage.attribution && (
						<div className="m-attr">
						  <a
							href={heroImage.original_source_url}
							target="_blank"
							rel="noopener noreferrer"
						  >
							{heroImage.attribution}
						  </a>
						</div>
					  )}
					</div>
				  )}
				  {/* TEXT CONTENT */}
				  <div className="m-body">
					<div className="card-header-flex" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginBottom: '7px' }}>
					  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
						<div style={{ width: '22px', height: '22px', backgroundColor: '#38CEF5', borderRadius: '50%', border: '1px solid #01A2DE', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
						  <span style={{ fontSize: '11px', fontFamily: 'Arial', color: 'black', fontWeight: 'bold' }}>{place.map_id}</span>
						</div>
						<span style={{ fontSize: '10px', fontFamily: 'Arial', color: '#666', marginTop: '3px', display: 'block', whiteSpace: 'nowrap' }}>AI Pick</span>
					  </div>
					  <div style={{ flexShrink: 0 }}>
						<h3 className="m-name">{place.title}</h3>
						<p className="m-place">{place.city}</p>
					  </div>
					</div>
					<p className="m-desc">{place.description}</p>
					<div className="m-reason">{place.fullReasoning}</div>
				  </div>
				</div>
			  );
			}

			// 3. CURATED PICK (editorial My Pick / Our Pick card, matches capture page layout)
			if (item.type === 'CURATED_PICK') {
			  const place = item.data;
			  const heroImage = place.images && place.images.length > 0 ? place.images[0] : null;
			  const additionalImages = place.images && place.images.length > 1 ? place.images.slice(1) : [];
			  const isSelected = selectedPlace && selectedPlace.place_id === place.place_id;
			  const categoryLabel = place.categories?.[0]?.name || 'Our Pick';

			  return (
				<div
				  key={item.id}
				  data-place-id={place.place_id}
				  className={`article-element-class m-card ai-place-card ${isSelected ? 'selected' : ''}`}
				  onClick={() => onItemClick && onItemClick(place)}
				  style={{ cursor: 'pointer' }}
				>
				  {heroImage && heroImage.image_url && (
					<div>
					  <img
						src={heroImage.image_url}
						className="ai-card-img"
						alt={heroImage.alt_text || place.title}
						style={{ width: '100%', display: 'block' }}
						onError={(e) => ((e.target as HTMLElement).parentElement!.style.display = 'none')}
					  />
					  {heroImage.attribution && (
						<div className="m-attr">
						  <a href={heroImage.original_source_url} target="_blank" rel="noopener noreferrer">
							{heroImage.attribution}
						  </a>
						</div>
					  )}
					</div>
				  )}
				  <div className="m-body">
					<div className="card-header-flex" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginBottom: '7px' }}>
					  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
						<div style={{ width: '22px', height: '22px', backgroundColor: '#38CEF5', borderRadius: '50%', border: '1px solid #01A2DE', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
						  <span style={{ fontSize: '11px', fontFamily: 'Arial', color: 'black', fontWeight: 'bold' }}>{place.map_id}</span>
						</div>
						<span style={{ fontSize: '10px', fontFamily: 'Arial', color: '#666', marginTop: '3px', display: 'block', whiteSpace: 'nowrap' }}>{categoryLabel}</span>
					  </div>
					  <div style={{ flexShrink: 0 }}>
						<h3 className="m-name">{place.title}</h3>
						<p className="m-place">{place.city}</p>
					  </div>
					</div>
					{place.description && (
					  <p className="m-desc" dangerouslySetInnerHTML={{ __html: place.description }} />
					)}
					{place.reason && <div className="m-reason">{place.reason}</div>}
					{additionalImages.map((img: any, i: number) => (
					  <div key={i} style={{ margin: '12px 8px 4px' }}>
						<img
						  src={img.image_url}
						  alt={img.alt_text || place.title}
						  style={{ width: '100%', display: 'block' }}
						  onError={(e) => ((e.target as HTMLElement).parentElement!.style.display = 'none')}
						/>
						{img.attribution && (
						  <p className="m-attr" style={{ marginTop: '4px' }}>
							<a href={img.original_source_url} target="_blank" rel="noopener noreferrer">{img.attribution}</a>
						  </p>
						)}
					  </div>
					))}
					{place.tags && place.tags.length > 0 && (
					  <div className="card-tags">
						{place.tags.map((tag: string) => {
						  const cfg = TAG_CONFIG[tag];
						  if (!cfg) return null;
						  return (
							<span key={tag} className={`card-tag ${tag}`}>
							  <i className={`ti ${cfg.icon}`} aria-hidden="true"></i> {cfg.label}
							</span>
						  );
						})}
					  </div>
					)}
				  </div>
				</div>
			  );
			}

			// 4. TRIP OVERVIEW / AI SUMMARY
			if (item.type === 'AI_SUMMARY') {
			return (
			  <div key={item.id} className="article-element-class m-quote-card summary-card">
				<h3>Trip Overview</h3>
				<p>{item.data.summary}</p>
				<hr />
				<p style={{ fontWeight: 500 }}>
				  Searched for Gems near: {item.data.midpoint_town} on the {item.data.inferred_road} route
				</p>
				<p>
				  Tap the map and zoom to see places or tap a different route to see alternative suggestions
				</p>
			  </div>
			);
			}

			// 4. SECTION DIVIDER
			if (item.type === 'SECTION_DIVIDER') {
			  return (
				<div key={item.id} style={{ width: 'calc(100% - 30px)', padding: '8px 0 4px' }}>
				  <div className="section-divider">
					<div className="divider-line" />
					<span className="divider-label">{item.label}</span>
					<div className="divider-line" />
				  </div>
				</div>
			  );
			}

			return null;
        })}
      </Masonry>
      {items.length > 0 && (
        <div className="poweredby">
          Powered by Google
        </div>
      )}
    </div>
  );
};

export default PlacesMasonryGallery;